import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from './ui';

const TIER_DETAILS = {
  BASIC: {
    name: 'Basic',
    price: '$9.99/mo',
    features: [
      'Unlimited Vision Journal & Story Bank',
      '20 content generations/month',
      '10 Content Studio posts/month',
      'Connect posting & messaging',
      'Body AI weekly insight',
      'Full Learn content library',
      'Health Vault access',
      'Book coaching sessions',
      'My Business deal pipeline',
    ],
  },
  PREMIUM: {
    name: 'Premium',
    price: '$19.99/mo',
    features: [
      'Everything in Basic, plus:',
      'Unlimited content generation',
      'Unlimited Content Studio posts',
      'Health Vault AI summaries',
      'Full analytics dashboard',
      'Up to 3 linked parent accounts',
    ],
  },
  AGENCY: {
    name: 'Agency',
    price: '$49.99/mo',
    features: [
      'Everything in Premium, plus:',
      'Full analytics with admin view',
      'Unlimited linked accounts',
      'Priority support',
    ],
  },
};

const FEATURE_LABELS = {
  connect_post: 'post and message on Connect',
  connect_message: 'message on Connect',
  body_ai_insight: 'get AI-powered weekly insights',
  health_vault: 'access your Health Vault',
  health_vault_ai: 'get AI health summaries',
  coaching_book: 'book coaching sessions',
  my_business_deals: 'track your business deals',
  content_generation: 'generate more content',
  content_studio_posts: 'publish more content',
  learn_content: 'access more learning content',
  vision_journal: 'add more journal entries',
  story_bank: 'save more stories',
};

export default function UpgradeModal({ isOpen, onClose, feature, requiredTier = 'BASIC', currentTier = 'FREE' }) {
  const navigate = useNavigate();
  const tier = TIER_DETAILS[requiredTier] || TIER_DETAILS.BASIC;
  const featureLabel = FEATURE_LABELS[feature] || 'unlock this feature';

  const handleUpgrade = () => {
    onClose();
    navigate('/settings/billing');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-prism-cyan/20 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-prism-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>

        <h2 className="font-display text-xl text-prism-text mb-2">
          Upgrade to {tier.name}
        </h2>
        <p className="text-prism-text-secondary text-sm mb-6">
          To {featureLabel}, upgrade to PRISM {tier.name} for just {tier.price}.
        </p>

        <div className="bg-prism-dark-secondary rounded-xl p-4 mb-6 text-left">
          <p className="text-xs text-prism-text-secondary uppercase tracking-wider mb-3">
            What you'll unlock
          </p>
          <ul className="space-y-2">
            {tier.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-prism-text">
                <svg className="w-4 h-4 text-prism-success flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {f}
              </li>
            ))}
          </ul>
        </div>

        <button
          onClick={handleUpgrade}
          className="w-full py-3 bg-gradient-cyan text-prism-dark font-semibold rounded-button shadow-glow-cyan hover:shadow-lg transition-all"
        >
          Upgrade to {tier.name} — {tier.price}
        </button>

        <button
          onClick={onClose}
          className="mt-3 text-sm text-prism-text-secondary hover:text-prism-text transition-colors"
        >
          Maybe later
        </button>
      </div>
    </Modal>
  );
}
